/**
 * Reduction Modal (Kapitalreduktion)
 *
 * Modal for registering capital reduction transactions.
 * Capital can be reduced for:
 * - Loss coverage (täckning av förlust)
 * - Shareholder repayment (återbetalning till aktieägare)
 * - Transfer to reserve fund (avsättning till reservfond)
 */

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import {
  useShareClasses,
  useShareholdersSummary,
  useShareholderHoldings,
  useRegisterReduction,
} from "@/hooks/use-share-book";
import type { ShareClass, ShareholderWithHoldings, ShareHolding } from "@/lib/api";

interface ReductionModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

const REDUCTION_REASONS = [
  {
    value: "loss_coverage",
    label: "Täckning av förlust",
    description: "Aktiekapitalet minskas för att täcka förlust",
  },
  {
    value: "shareholder_repayment",
    label: "Återbetalning till aktieägare",
    description: "Aktiekapitalet minskas med återbetalning till ägare",
  },
  {
    value: "reserve_fund",
    label: "Avsättning till reservfond",
    description: "Aktiekapitalet minskas genom överföring till fritt eget kapital",
  },
] as const;

const reductionSchema = z.object({
  shareClassId: z.string().min(1, "Välj aktieslag"),
  shareholderId: z.string().min(1, "Välj aktieägare"),
  numberFrom: z.coerce.number().min(1, "Aktienummer från krävs"),
  numberTo: z.coerce.number().min(1, "Aktienummer till krävs"),
  reductionReason: z.enum(["loss_coverage", "shareholder_repayment", "reserve_fund"]),
  amountPerShare: z.coerce.number().min(0, "Belopp per aktie kan inte vara negativt"),
  transactionDate: z.string().min(1, "Transaktionsdatum krävs"),
  notes: z.string().optional(),
}).refine((data) => data.numberTo >= data.numberFrom, {
  message: "Aktienummer till måste vara större än eller lika med från",
  path: ["numberTo"],
});

type ReductionFormData = z.infer<typeof reductionSchema>;

export function ReductionModal({
  isOpen,
  onClose,
  companyId,
}: ReductionModalProps) {
  const { data: shareClasses = [] } = useShareClasses(companyId);
  const { data: shareholders = [] } = useShareholdersSummary(companyId);
  const registerReduction = useRegisterReduction();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReductionFormData>({
    resolver: zodResolver(reductionSchema),
    defaultValues: {
      shareClassId: "",
      shareholderId: "",
      numberFrom: undefined,
      numberTo: undefined,
      reductionReason: "loss_coverage",
      amountPerShare: 0,
      transactionDate: new Date().toISOString().split("T")[0],
      notes: "",
    },
  });

  const watchShareClassId = watch("shareClassId");
  const watchShareholderId = watch("shareholderId");
  const watchNumberFrom = watch("numberFrom");
  const watchNumberTo = watch("numberTo");
  const watchAmountPerShare = watch("amountPerShare");
  const watchReason = watch("reductionReason");

  // Fetch holdings for selected shareholder
  const { data: holdings = [] } = useShareholderHoldings(
    watchShareholderId || undefined
  );

  // Filter shareholders that have holdings in selected share class
  const shareholdersWithHoldings = useMemo(() => {
    if (!watchShareClassId) return [];
    return shareholders.filter((s: ShareholderWithHoldings) =>
      s.isActive &&
      s.holdings.some((h) => h.shareClass.id === watchShareClassId && h.totalShares > 0)
    );
  }, [shareholders, watchShareClassId]);

  // Get available holdings for selected shareholder in selected share class
  const availableHoldings = useMemo(() => {
    if (!watchShareClassId || !holdings.length) return [];
    return holdings.filter((h: ShareHolding) => h.shareClassId === watchShareClassId);
  }, [holdings, watchShareClassId]);

  // Calculate quantity and total amount
  const calculatedQuantity = useMemo(() => {
    if (!watchNumberFrom || !watchNumberTo) return 0;
    return Math.max(0, watchNumberTo - watchNumberFrom + 1);
  }, [watchNumberFrom, watchNumberTo]);

  const totalAmount = useMemo(() => {
    return calculatedQuantity * (watchAmountPerShare || 0);
  }, [calculatedQuantity, watchAmountPerShare]);

  const onSubmit = async (data: ReductionFormData) => {
    try {
      await registerReduction.mutateAsync({
        companyId,
        data: {
          shareClassId: data.shareClassId,
          shareholderId: data.shareholderId,
          quantity: calculatedQuantity,
          numberFrom: data.numberFrom,
          numberTo: data.numberTo,
          reductionReason: data.reductionReason,
          amountPerShare: data.amountPerShare,
          transactionDate: data.transactionDate,
          notes: data.notes,
        },
      });
      toast.success(
        `Kapitalreduktion registrerad - ${calculatedQuantity.toLocaleString("sv-SE")} aktier indragna`
      );
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte registrera kapitalreduktion";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Kapitalreduktion"
      size="lg"
    >
      <div className="space-y-6">
        {/* Warning */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-amber-900">Viktigt om kapitalreduktion</p>
              <p className="text-sm text-amber-700 mt-1">
                Kapitalreduktion kräver beslut av bolagsstämma och i vissa fall
                tillstånd från Bolagsverket. Det kan även kräva kallelse på okända
                borgenärer. Se till att alla juridiska krav är uppfyllda.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Reduction Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ändamål med kapitalreduktionen *
            </label>
            <div className="space-y-2">
              {REDUCTION_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    watchReason === reason.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    {...register("reductionReason")}
                    value={reason.value}
                    className="mt-1"
                  />
                  <div>
                    <p
                      className={`font-medium ${
                        watchReason === reason.value
                          ? "text-blue-900"
                          : "text-gray-900"
                      }`}
                    >
                      {reason.label}
                    </p>
                    <p className="text-xs text-gray-500">{reason.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Share Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieslag *
            </label>
            <select {...register("shareClassId")} className="input">
              <option value="">Välj aktieslag</option>
              {shareClasses.map((sc: ShareClass) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} ({sc.code}) - {sc.totalShares.toLocaleString("sv-SE")} aktier
                </option>
              ))}
            </select>
            {errors.shareClassId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.shareClassId.message}
              </p>
            )}
          </div>

          {/* Shareholder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieägare *
            </label>
            <select
              {...register("shareholderId")}
              className="input"
              disabled={!watchShareClassId}
            >
              <option value="">Välj aktieägare</option>
              {shareholdersWithHoldings.map((s: ShareholderWithHoldings) => {
                const holding = s.holdings.find(
                  (h) => h.shareClass.id === watchShareClassId
                );
                return (
                  <option key={s.id} value={s.id}>
                    {s.name} ({holding?.totalShares.toLocaleString("sv-SE") || 0} aktier)
                  </option>
                );
              })}
            </select>
            {errors.shareholderId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.shareholderId.message}
              </p>
            )}
          </div>

          {/* Available Holdings Info */}
          {watchShareholderId && availableHoldings.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">Tillgängliga aktier:</p>
                  <ul className="mt-1 space-y-1">
                    {availableHoldings.map((h: ShareHolding, idx: number) => (
                      <li key={idx} className="text-blue-700">
                        Nr {h.numberFrom} - {h.numberTo} ({h.numberTo - h.numberFrom + 1} st)
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Share Number Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktienummer från *
              </label>
              <input
                type="number"
                {...register("numberFrom")}
                className="input"
                min={1}
              />
              {errors.numberFrom && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.numberFrom.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktienummer till *
              </label>
              <input
                type="number"
                {...register("numberTo")}
                className="input"
                min={1}
              />
              {errors.numberTo && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.numberTo.message}
                </p>
              )}
            </div>
          </div>

          {calculatedQuantity > 0 && (
            <p className="text-sm text-gray-600">
              Antal aktier som indras:{" "}
              <span className="font-medium">
                {calculatedQuantity.toLocaleString("sv-SE")}
              </span>
            </p>
          )}

          {/* Amount per Share (only for shareholder repayment) */}
          {watchReason === "shareholder_repayment" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Belopp per aktie (kr) *
              </label>
              <input
                type="number"
                step="0.01"
                {...register("amountPerShare")}
                className="input"
                min={0}
              />
              <p className="text-xs text-gray-500 mt-1">
                Belopp som återbetalas till aktieägaren per aktie
              </p>
              {errors.amountPerShare && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.amountPerShare.message}
                </p>
              )}
            </div>
          )}

          {/* Total amount preview */}
          {watchReason === "shareholder_repayment" &&
            calculatedQuantity > 0 &&
            watchAmountPerShare > 0 && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total återbetalning:</span>
                  <span className="font-medium text-gray-900">
                    {totalAmount.toLocaleString("sv-SE")} kr
                  </span>
                </div>
              </div>
            )}

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaktionsdatum *
            </label>
            <input
              type="date"
              {...register("transactionDate")}
              className="input"
            />
            {errors.transactionDate && (
              <p className="text-sm text-red-600 mt-1">
                {errors.transactionDate.message}
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anteckningar
            </label>
            <textarea
              {...register("notes")}
              className="input min-h-[80px]"
              placeholder="Valfria anteckningar om kapitalreduktionen..."
            />
          </div>

          {/* Legal info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Observera:</strong> Kapitalreduktion registreras hos
              Bolagsverket och träder inte i kraft förrän registreringen är
              genomförd.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || calculatedQuantity === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Registrerar...
                </>
              ) : (
                "Registrera kapitalreduktion"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
